import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectClientAddress } from "../../store/userSlice";
import { toCoord } from "../utils/geo";

const asCoords = (latitude, longitude) => {
  // toCoord, not Number: a blank saved coordinate would otherwise read as 0 and
  // place the user on the equator instead of falling through to the next source.
  const lat = toCoord(latitude);
  const lon = toCoord(longitude);
  return Number.isFinite(lat) && Number.isFinite(lon) ? { latitude: lat, longitude: lon } : null;
};

/**
 * The coordinate ladder, as a pure function — freshest intent first:
 *   1. clientAddress — the location committed by the user this or a previous visit:
 *                      a GPS fix or a hand-picked address, last write wins
 *                      (localStorage; `source` says which kind it is)
 *   2. userData      — the address saved on their profile, from the Login response
 *   3. null          — callers must tolerate this; GetCarousels simply omits
 *                      Latitude/Longitude and the distance chips drop out.
 *
 * Exported alongside the hook (the same split as derivePassState) so a handler can
 * recompute this from the live store *after* an await, rather than using the value
 * its closure captured. That matters because rung 2 arrives with the Login response:
 * a handler that read `coords` before Login landed would conclude the user has no
 * location at all and send them to the explainer they don't need.
 *
 * @param {{clientAddress: Object, userData: Object}} sources
 * @returns {{source: "gps"|"address"|"profile"|"none", coords: {latitude: number, longitude: number}|null}}
 */
export const deriveUserCoords = ({ clientAddress, userData }) => {
  const saved = asCoords(clientAddress?.latitude, clientAddress?.longitude);
  if (saved) return { source: clientAddress.source ?? "address", coords: saved };

  const profile = asCoords(userData?.Latitude, userData?.Longitude);
  if (profile) return { source: "profile", coords: profile };

  return { source: "none", coords: null };
};

/**
 * The user's coordinates, read from the store — a pure reader with no side
 * effects. Prompting lives in requestLocation(), called by the welcome gate, the
 * location screen and the address screen's "current location" row (tap-initiated),
 * so no screen can raise a permission dialog by rendering.
 *
 * All sources are hydrated or dispatched before the first render of any screen that
 * reads them, so `coords` is available synchronously and `status` is always "ready"
 * — it is kept only so existing call sites don't have to change.
 *
 * @returns {{
 *   status: "ready",
 *   source: "gps"|"address"|"profile"|"none",
 *   coords: {latitude: number, longitude: number}|null,
 * }}
 */
export default function useUserLocation() {
  const clientAddress = useSelector(selectClientAddress);
  const userData = useSelector((state) => state.userSlice.userData);

  return useMemo(() => {
    const { source, coords } = deriveUserCoords({ clientAddress, userData });
    return { status: "ready", source, coords };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientAddress?.latitude,
    clientAddress?.longitude,
    userData?.Latitude,
    userData?.Longitude,
  ]);
}
